import {
  Alert,
  AmountInput,
  Card,
  CardBody,
  Form,
  FormCol,
  FormField,
  FormRow,
  Heading,
  Paragraph,
  Separator,
  SliderInput,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import {
  CreateCaseOnlineRq,
  ProductInstanceForOfferDTO,
} from "@smartx/mikro-vt-client-api"
import { useFormik } from "formik"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import * as Yup from "yup"
import { createCaseOnlineApi } from "../../api/apiClient"
import { useOfferData, useSelectedOffer } from "../../app/hooks"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import AuthContext from "../../providers/AuthProvider"
import { DataListComponent } from "../../Shared"
import { RequestHeader } from "../../utils/utils"
import { OfferAmountComponent, OfferFlag } from "./offerHelperComponents"
import { useSaveModal } from "./OfferModalContext"
import { OfferNavButtons } from "./OfferNavButtons"
import { useOfferHelperUtil } from "./useOfferHelperUtil"

const OfferCalculator = () => {
  const authContext = useContext(AuthContext)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offerHelper = useOfferHelperUtil()
  const offer = useSelectedOffer()
  const offerData = useOfferData()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const { openErrorModal } = useSaveModal()
  const [product] = useState<ProductInstanceForOfferDTO>(
    offerData?.productInstances?.find((el) => !!el),
  )
  const maxAmount = product?.requestedAmount

  const formik = useFormik({
    initialValues: { amount: product?.requestedAmount },
    validationSchema: Yup.object({
      amount: Yup.number()
        .required()
        .min(
          product?.pricingParamsForOfferDTO?.amountMin,
          t("offers.calculator.warning"),
        )
        .max(maxAmount, t("offers.calculator.warning")),
    }),
    onSubmit: (values) => handleSubmit(values),
  })

  const [localAmount, setLocalAmount] = useState(formik.values.amount)

  useEffect(() => {
    setLocalAmount(formik.values.amount)
  }, [formik.values.amount])

  const getAmountByEvent = (val: string) => +(val?.replaceAll(" ", "") || 0)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = getAmountByEvent(event?.target?.value)
    setLocalAmount(value)
  }

  const handleBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
    formik.setFieldTouched("amount", true)

    const step = product?.pricingParamsForOfferDTO?.amountStep
    let newAmount = localAmount || 0

    if (step) {
      const diff = newAmount % step
      newAmount = newAmount - diff
    }

    formik.setFieldValue("amount", newAmount, true)
    setLocalAmount(newAmount)
  }

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = getAmountByEvent(event?.target?.value)
    formik.setFieldValue("amount", value)
  }

  const createCase = async () => {
    if (!offer?.sourceId) {
      openErrorModal(() => navigate("/dashboard", { replace: true }))
      return
    }

    authContext.refresh()

    const rq: CreateCaseOnlineRq = {
      header: new RequestHeader(),
      loanAmount: formik.values.amount,
      sourceId: offer.sourceId,
    }

    try {
      const result = await createCaseOnlineApi.createCaseOnline(rq)
      if (!result?.data?.caseId) {
        throw new Error("Create case error")
      }

      goToNextOfferScreen(result?.data?.caseId)
    } catch {
      openErrorModal(() => navigate("/dashboard", { replace: true }))
    }
  }

  const handleSubmit = (_values: any) => {
    createCase()
  }

  const fieldArr = offerHelper.getPricingFields(product)

  const headerContent = (
    <Heading level={1}>{t("offers.calculator.title")}</Heading>
  )

  const textContent = (
    <Paragraph className="mb-3">{t("offers.calculator.text")}</Paragraph>
  )

  const detailsTextContent = (
    <Heading level={2}>{t("offers.calculator.details")}</Heading>
  )

  const navButtonsContent = (
    <OfferNavButtons
      prevButtonText={t("offers.button.back")}
      onPrevButtonClicked={() =>
        navigate("/offer/info/welcome", { replace: true })
      }
    />
  )

  const minAmountContent = (
    <OfferAmountComponent
      amount={product?.pricingParamsForOfferDTO?.amountMin}
    />
  )

  const maxAmountContent = <OfferAmountComponent amount={maxAmount} />

  const flagContent = <OfferFlag />

  const minMaxContentXs = (
    <Paragraph className="text-center">
      {t("offers.calculator.min")}: {minAmountContent}
      <span className="pl-2 pr-2"> - </span>
      {t("offers.calculator.max")}: {maxAmountContent}
    </Paragraph>
  )

  const calculatorContentXs = (
    <Card>
      <FormField
        input={
          <FormRow>
            <FormCol>
              <FormField
                input={
                  <AmountInput
                    style={{
                      textAlign: "center",
                      border: "none",
                      backgroundImage: "none",
                    }}
                    precision={0}
                    formatOnChange={true}
                    thousandSeparator=" "
                    name="amount"
                    value={localAmount}
                    onInput={() => {}}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                required
                variant={FormField.VARIANT.LARGE}
                style={{
                  marginBottom: "0px",
                }}
              />
            </FormCol>

            <FormCol className="text-center">{flagContent}</FormCol>
          </FormRow>
        }
        required
      />

      {minMaxContentXs}
    </Card>
  )

  const warning = (
    <>
      {!formik.isValid && !!formik.submitCount && (
        <Alert heading="" variant={Alert.VARIANT.WARNING}>
          {formik.errors.amount}
        </Alert>
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {calculatorContentXs}

        {warning}

        {detailsTextContent}

        <Card padding={Card.PADDING.NONE}>
          <DataListComponent dataList={fieldArr} displayAsLabeledList={true} />
        </Card>

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          <FormField
            description={
              <SliderInput
                startLabel={minAmountContent}
                endLabel={maxAmountContent}
                min={product?.pricingParamsForOfferDTO?.amountMin}
                max={maxAmount}
                step={product?.pricingParamsForOfferDTO?.amountStep}
                value={localAmount}
                onChange={handleSliderChange}
              />
            }
            input={
              <AmountInput
                precision={0}
                formatOnChange={true}
                thousandSeparator=" "
                name="amount"
                value={localAmount}
                onInput={() => {}}
                onChange={handleChange}
                onBlur={handleBlur}
                flag={flagContent}
              />
            }
            label={t("offers.calculator.loanAmount")}
            labelPosition={FormField.LABEL_POSITION.INSIDE}
            required
            variant={FormField.VARIANT.LARGE}
          />

          {warning}
        </CardBody>

        <Separator />

        <CardBody>
          {detailsTextContent}

          <DataListComponent
            dataList={fieldArr}
            displayAsLabeledList={true}
            isStripedLabeledList={true}
          />
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  if (!product) {
    return <></>
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      {pageContent}
    </Form>
  )
}

export default OfferCalculator
